import React, { useRef, useState } from 'react'
import { View, ScrollView, Image, Platform } from 'react-native'
import * as Linking from 'expo-linking'
import { StackNavigationProp } from '@react-navigation/stack'

import tw from 'tailwind-rn'
import { useSelector } from 'react-redux'
import styles from './styles'
import { Markdown } from '../../components/Content'
import {
  HGView,
  HGGroup,
  HGText,
  HGFlatList,
  HGButton,
} from '../../components/HGUI'
import { RootStackParamList } from '../../navigators/AppNavigator'
import CrossPlatformImage from '../../components/CrossPlatformImage'
import { getAssetUrl } from '../../lib/utils'

const check = require('../../../assets/img/other/check.png')

const copy0 = `## Hinweise sammeln durch Anamnese und Untersuchung:
Körperlich/Psychisch/Verhalten/Familiensystem (z.B. Umgang mit Kind, Gesundheitsfürsorge für das Kind, Gesundheitsstatus der Eltern, besondere Belastung?)

Keine "Ermittlungsarbeit", aber Ihre Sorgen begründen.`
const copy1 = `## Dranbleiben
                
- Elterngespräch
- Hilfsangebote
- zeitliche Festlegung
- Aufzeigen von Konsequenzen
- weitere Diagnostik anregen
- Indikatoren für Verbesserung/Verschlechterung transparent aufzeigen
- Falls möglich, Schweigepflichtsentbindung durch Eltern einholen
- Wiederbestellung: Überprüfung von vorherigen Absprachen (z.B. weitere Diagnostikaufträge)
- interdisziplinäre Beratung mit insoweit erfahrener Fachkraft`
const copy2 = `## Einschätzung der Eltern
- **Kooperativ** ("Bereitschaft"), adäquate Reaktionen auf Angebot und Sorge, Pünktlichkeit
- **Kompetenz** ("Fähigkeit"), familiäre Belastungsfaktoren, Gesundheitsstatus, Sprachverständnis`
const copy3_1 = `Begleiten Sie den weiteren Fortgang und bestellen Sie den Patienten wieder ein, um die Risikoeinschätzung zu aktualisieren.

Vergessen Sie nicht jeden Schritt zu dokumentieren. Den Dokumenationsbogen können Sie hier herunterladen:`
const copy3_2 = `## Informieren Sie die Eltern über die Meldung an das Jugendamt. Tauschen Sie sich fallbezogen mit anderen Fachkräften aus, um das weitere Vorgehen zu planen!

Nutzen Sie dazu den Meldebogen und wenden Sie sich an das zuständige Jugendamt.`

type Props = {
  navigation: StackNavigationProp<RootStackParamList>
}

const SuspicionCheckerLatentDangerChildrenScreen = (props: Props) => {
  const { navigation } = props
  const globals = useSelector((state) => state.globals)
  const [currentStage, setCurrentStage] = useState<string>('0')
  const scrollView = useRef(null)
  // openModal(route: string) {}

  const enterStage = (stage: string) => {
    setCurrentStage(stage)
    scrollView.current.scrollTo({ y: 0, animated: true })
  }

  return (
    <HGView>
      <ScrollView ref={scrollView}>
        <View style={tw('pt-6 px-3')}>
          {/* 0 */}
          {currentStage === '0' && (
            <View>
              <View style={tw('mb-5')}>
                <Markdown content={copy0} />
              </View>

              <View style={styles.bottomActionBox}>
                <HGButton
                  title="Nächster Schritt"
                  onPress={() => enterStage('1')}
                />
              </View>
            </View>
          )}

          {/* 1 */}
          {currentStage === '1' && (
            <View>
              <View style={tw('mb-5')}>
                <Markdown content={copy1} />
              </View>

              <View style={styles.bottomActionBox}>
                <HGButton
                  title="Nächster Schritt"
                  onPress={() => enterStage('2')}
                />
              </View>
            </View>
          )}

          {/* 2 */}
          {currentStage === '2' && (
            <View>
              <View style={tw('mb-5')}>
                <Markdown>## Entscheidung</Markdown>
              </View>

              <HGGroup>
                <HGText style={styles.questionText}>
                  Sind die Sorgeberechtigten ausreichend kooperativ und
                  kompetent?
                </HGText>
              </HGGroup>

              <View style={styles.actionBox}>
                <HGButton
                  style={styles.buttonHalfWidth}
                  title="Ja"
                  onPress={() => enterStage('3-1')}
                />
                <HGButton
                  style={styles.buttonHalfWidth}
                  type="inverted"
                  title="Nein"
                  onPress={() => enterStage('3-2')}
                />
              </View>

              <Markdown content={copy2} />
            </View>
          )}

          {/* 3-1 */}
          {currentStage === '3-1' && (
            <View>
              <View style={[styles.successBox, tw('mb-5')]}>
                <View style={tw('mr-3')}>
                  <CrossPlatformImage source={check} style={tw('w-5 h-5')} />
                </View>
                <View style={{ flex: 1, flexWrap: 'wrap' }}>
                  <Markdown>
                    ### Danke für Ihr Engagement im Kinderschutz!
                  </Markdown>
                </View>
              </View>

              <View style={tw('mb-5')}>
                <Markdown content={copy3_1} />
              </View>

              <HGFlatList
                scrollEnabled={false}
                data={[
                  {
                    title: 'Dokumentationsbogen',
                    type: 'download',
                    key: 'chapter-download1',
                    onPress: () =>
                      Linking.openURL(getAssetUrl(globals?.documentationForm)),
                  },
                ]}
                keyExtractor={(item) => item.key}
              />

              <View style={styles.bottomActionBox}>
                <HGButton
                  title="Zurück zur Dokumentation"
                  onPress={() =>
                    navigation.reset({
                      routes: [{ name: 'Documentation' }],
                    })
                  }
                />
              </View>
            </View>
          )}

          {/* 3-2 */}
          {currentStage === '3-2' && (
            <View>
              <View style={tw('mb-5')}>
                <Markdown>{copy3_2}</Markdown>
              </View>

              <HGFlatList
                scrollEnabled={false}
                data={[
                  {
                    title: 'Meldestellen',
                    description: 'Jugendämter',
                    type: 'link',
                    key: 'chapter-advicecenter',
                    onPress: () =>
                      navigation.push('GenericResource', {
                        resourceId: '38',
                        slug: 'meldestellen',
                        ...(Platform.OS !== 'web' && { name: 'Meldestellen' }),
                      }), // this.openModal('ChapterAdviceCenterScreen'),
                  },
                  {
                    title: 'Meldebogen',
                    type: 'download',
                    key: 'chapter-download1',
                    onPress: () => Linking.openURL(globals?.reportingForm),
                  },
                ]}
                keyExtractor={(item) => item.key}
              />

              <View style={styles.bottomActionBox}>
                <HGButton
                  title="Nächster Schritt"
                  onPress={() => enterStage('3-1')}
                />
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </HGView>
  )
}

export default SuspicionCheckerLatentDangerChildrenScreen
